/**
 * DriverRunsView - List-focused driver view (no map)
 * 
 * Mobile-optimized view for drivers showing:
 * - Assigned runs for each date
 * - Stop list with full details
 * - Google Maps link for navigation
 * - "Resume from here" functionality for mid-route restart
 * - Mark Delivered functionality with POD capture
 * 
 * This is an alternative to the map-based DriverView for comparison.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useShopSettings } from '@/context/ShopSettingsContext';
import { selectShopRouteOrigin } from '@/utils/shopRouteOrigin';
import { 
  Truck, Phone, Clock, CheckCircle, RotateCcw, Bell, X, Pencil, Route, Package, CloudOff, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import DriverDeliveryHistory from '@/components/driver/DriverDeliveryHistory';
import SavedDeliveryReviewList from '@/components/driver/SavedDeliveryReviewList';
import GoogleMapsLinks from '@/components/GoogleMapsLinks';
import DeliveryConfirmationSheet from '@/components/DeliveryConfirmationSheet';
import SavedPhotoRecoverySheet from '@/components/delivery/SavedPhotoRecoverySheet';
import FuelCostEstimator from '@/components/FuelCostEstimator';
import AccessCodesSection from '@/components/driver/AccessCodesSection';
import { savedAccessAddressKey } from '@/utils/accessCodeContext';
import ArrangementPhotoThumbnail from '@/components/ArrangementPhotoThumbnail';
import { buildResumeRouteFromStop, getFullAddress, isGoogleMapsDirectionsUrl } from '@/utils/googleMapsUrl';
import { getAllDeliveries } from '@/utils/deliveryOutbox';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';
import useRunDistance from '@/hooks/useRunDistance';
import useRunsStore from '@/hooks/useRunsStore';
import { isDesktopOffline } from '@/api/desktopSync';
import { getMyRuns } from '@/api/runs';
import { getActiveSessionRevision } from '@/api/authenticatedClient';
import { deliveryPrincipalKey, listDeliveryWorkspaces, hasDeliveryWorkspaceWork,
  subscribeDeliveryWorkspaces, getDeliveryWorkspaceRevision } from '@/utils/deliveryDraftStore';
import { DELIVERY_PHOTO_PREPARATION_ENABLED } from '@/hooks/useDeliveryPhotoPreparation';
import { listPhotoDrafts } from '@/utils/deliveryPhotoDraftPersistence';

// Format date for display
const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Get today's date string in local timezone
const localDateString = (today) => {
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const getTodayString = () => localDateString(new Date());
const NO_STOPS = [];

// Generate date options (today + next 6 days) in local timezone
const getDateOptions = (todayString) => {
  const dates = [];
  const today = new Date(`${todayString}T00:00:00`);
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }
  return dates;
};

const DriverRunsView = () => {
  const { getToken, sessionBinding } = useAuth();
  const { routeOrigin } = useShopSettings() || {};
  const shopOrigin = selectShopRouteOrigin(routeOrigin);
  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [today, setToday] = useState(getTodayString);
  const [pastCalendarOpen, setPastCalendarOpen] = useState(false);
  const isHistorical = selectedDate < today;
  useEffect(() => {
    let timer;
    const refreshDay = () => {
      setToday(getTodayString());
      clearTimeout(timer);
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      timer = setTimeout(refreshDay, midnight.getTime() - now.getTime() + 20);
    };
    refreshDay();
    window.addEventListener('focus', refreshDay);
    document.addEventListener('visibilitychange', refreshDay);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', refreshDay);
      document.removeEventListener('visibilitychange', refreshDay);
    };
  }, []);
  const {
    runs, selectedRun, selectedRunId, setSelectedRunId, fetchRuns, captureRunRefresh,
    loadingRuns, hasLoadedRuns, runsError,
  } = useRunsStore(selectedDate, { driverMode: true, offline: isDesktopOffline() });
  
  // Delivery confirmation state
  const [confirmation, setConfirmation] = useState(null);
  const confirmationIsCurrent = !isHistorical && confirmation?.date >= today;
  const confirmingOrder = confirmationIsCurrent ? confirmation.order : null;
  const [historyOpening, setHistoryOpening] = useState(null);
  const [recoveryOpening, setRecoveryOpening] = useState(null);
  const recoveryOpeningRef = useRef(null);
  const confirmationRef = useRef(null);
  const openingRequest = useRef(null);
  const openingSequence = useRef(0);
  const currentContext = useRef(null);
  currentContext.current = { sessionBinding, selectedDate, selectedRunId };
  useEffect(() => {
    setHistoryOpening(null);
  }, [selectedDate, selectedRunId, sessionBinding]);
  useEffect(() => {
    recoveryOpeningRef.current = null;
    setRecoveryOpening(null);
  }, [sessionBinding]);
  useEffect(() => {
    if (isHistorical || (confirmation && confirmation.date < today)) {
      openingRequest.current = null;
      confirmationRef.current = null;
      setConfirmation(null);
    }
  }, [isHistorical, today, confirmation]);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      openingRequest.current = null;
    };
  }, []);
  // Capture the read continuation when opening, not on a later render/date.
  // The form owns its captured order independently of refreshed list objects.
  const openConfirmation = useCallback(async (order, preparedReview = false) => {
    const principal = deliveryPrincipalKey(sessionBinding);
    if (selectedDate < getTodayString() || !principal || sessionBinding.revision !== getActiveSessionRevision() || !order?.id) return;
    const binding = sessionBinding;
    const owningRun = selectedRun;
    const request = {};
    openingRequest.current = request;
    const owns = () => mounted.current && openingRequest.current === request
      && currentContext.current.sessionBinding === binding
      && currentContext.current.selectedDate === selectedDate
      && currentContext.current.selectedRunId === owningRun?.id
      && selectedDate >= getTodayString()
      && getActiveSessionRevision() === binding.revision;
    const refreshRuns = captureRunRefresh();
    let authorizedOrder = order;
    let hasPrepared = preparedReview;
    let hasFailureReport = false;
    if (DELIVERY_PHOTO_PREPARATION_ENABLED) {
      try {
        const [drafts, legacy] = await Promise.all([listPhotoDrafts(principal), getAllDeliveries()]);
        if (!owns()) return;
        hasPrepared = hasPrepared || drafts.some((draft) => draft.orderId === order.id);
        hasFailureReport = drafts.some((draft) => draft.orderId === order.id && draft.operation === 'failure');
        if (hasPrepared && legacy.some((copy) => copy?.orderId === order.id)) {
          toast.error('A legacy saved copy also exists for this order. Both are retained; review that copy before continuing.'); return;
        }
      } catch { if (owns()) toast.error('Saved work could not be checked. Nothing was submitted; retry opening after storage is available.'); return; }
    }
    if (hasPrepared || listDeliveryWorkspaces(principal, order.id).length) {
      // Retained lists are usable for navigation, but are not fresh recovery
      // authority. This read never publishes into the run-list projection.
      const token = getToken();
      if (!token || !owns()) return;
      try {
        const freshRuns = await getMyRuns(selectedDate, { headers: { Authorization: `Bearer ${token}` } });
        if (!owns()) return;
        const freshRun = freshRuns.find(run => run.id === owningRun?.id);
        const freshOrder = freshRun?.populated_stops?.find(stop => stop.order_id === order.id)?.order;
        if (!freshOrder || freshOrder.id !== order.id || freshRun.closed_at
          || (!hasPrepared && freshOrder.status !== order.status) || (freshOrder.status === 'failed' && !hasFailureReport)) {
          toast.error(hasPrepared ? 'This order is no longer eligible for the saved work. The device copy is retained. Refresh runs and review its current status.'
            : 'This order is no longer eligible for the same delivery work. Your unfinished work is retained in this tab. Refresh runs and review its current status.');
          return;
        }
        authorizedOrder = freshOrder;
      } catch {
        if (owns()) toast.error(hasPrepared ? 'Could not verify this order for recovery. The device copy is retained. Reconnect and try opening it again.'
          : 'Could not verify this order for recovery. Your unfinished work is retained in this tab. Reconnect and try opening it again.');
        return;
      }
    }
    if (!owns()) return;
    const opening = { order: authorizedOrder, date: selectedDate, refreshRuns, sessionBinding: binding, id: ++openingSequence.current };
    confirmationRef.current = opening;
    setConfirmation(opening);
    return true;
  }, [captureRunRefresh, getToken, selectedDate, selectedRun, sessionBinding]);

  // Saved confirmation work still needs manual review/completion. A missing
  // acknowledgement does not prove the original request was never received.
  const [unsentRecords, setUnsentRecords] = useState([]);
  const [preparedSnapshot, setPreparedSnapshot] = useState(null);
  const preparedPrincipal = deliveryPrincipalKey(sessionBinding);
  const workspaceRevision = useSyncExternalStore(subscribeDeliveryWorkspaces, getDeliveryWorkspaceRevision);
  const savedPreparedRecords = preparedSnapshot?.principal === preparedPrincipal ? preparedSnapshot.records : NO_STOPS;
  // Action leases notify this store too. Keep an unchanged list snapshot stable:
  // replacing it when deletion acquires its own lease retires that same consent.
  // Serialize only the small memory-only summaries, never fields or media.
  const memoryPreparedKey = useMemo(() => {
    if (!DELIVERY_PHOTO_PREPARATION_ENABLED || !preparedPrincipal) return '[]';
    const result = [];
    listDeliveryWorkspaces(preparedPrincipal).filter(hasDeliveryWorkspaceWork).forEach(work => {
      const kind = work.kind === 'failure' ? 'confirmation' : work.kind;
      if (!savedPreparedRecords.some(record => record.orderId === work.orderId && record.kind === kind)
          && !result.some(record => record.orderId === work.orderId && record.kind === kind)) {
        result.push({ orderId: work.orderId, kind, operation: work.kind,
          status: work.attempt ? 'unknown' : 'draft', memoryOnly: true, needsRecovery: true });
      }
    });
    return JSON.stringify(result);
    // Store revision announces immutable workspace replacement/removal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparedPrincipal, savedPreparedRecords, workspaceRevision]);
  const preparedRecords = useMemo(() => {
    const memory = JSON.parse(memoryPreparedKey);
    return memory.length ? [...savedPreparedRecords, ...memory] : savedPreparedRecords;
  }, [savedPreparedRecords, memoryPreparedKey]);
  const [outboxError, setOutboxError] = useState(false);
  const outboxRead = useRef(0);
  const loadOutbox = useCallback(async () => {
    const read = ++outboxRead.current;
    const binding = sessionBinding;
    const principal = deliveryPrincipalKey(binding);
    try {
      const [records, prepared] = await Promise.all([getAllDeliveries(),
        DELIVERY_PHOTO_PREPARATION_ENABLED && principal ? listPhotoDrafts(principal) : []]);
      if (!Array.isArray(records)) throw new Error('Invalid saved-delivery list');
      if (!mounted.current || read !== outboxRead.current || binding !== currentContext.current.sessionBinding
          || binding?.revision !== getActiveSessionRevision()) return;
      setUnsentRecords(records);
      setPreparedSnapshot({ principal, records: prepared });
      setOutboxError(false);
      return true;
    } catch {
      if (mounted.current && read === outboxRead.current && binding === currentContext.current.sessionBinding) setOutboxError(true);
      return false;
    }
  }, [sessionBinding]);
  useEffect(() => { loadOutbox(); return () => { outboxRead.current += 1; }; }, [loadOutbox]);
  useEffect(() => {
    if (!DELIVERY_PHOTO_PREPARATION_ENABLED) return undefined;
    const refresh = () => { void loadOutbox(); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [loadOutbox]);
  const reviewPrepared = (record) => {
    if (!DELIVERY_PHOTO_PREPARATION_ENABLED || !preparedRecords.includes(record) || outboxError
      || !preparedPrincipal || sessionBinding !== currentContext.current.sessionBinding
      || sessionBinding?.revision !== getActiveSessionRevision()
      || !['confirmation', 'amendment'].includes(record.kind) || typeof record.orderId !== 'string') return;
    const opening = { principal: preparedPrincipal, binding: sessionBinding, record, id: ++openingSequence.current };
    openingRequest.current = null;
    confirmationRef.current = null;
    setConfirmation(null);
    recoveryOpeningRef.current = opening;
    setRecoveryOpening(opening);
  };
  const ownsRecovery = (opening) => mounted.current && recoveryOpeningRef.current === opening
    && currentContext.current.sessionBinding === opening.binding
    && getActiveSessionRevision() === opening.binding?.revision;
  const continuePrepared = async (opening, record) => {
    if (!ownsRecovery(opening) || record.orderId !== opening.record.orderId) return false;
    const stop = selectedRun?.populated_stops?.find((item) => item.order_id === record.orderId && item.order?.id === record.orderId);
    if (!stop || selectedRun.closed_at || selectedDate < getTodayString()) {
      toast.error('Select the current assigned, open run to continue. Local review remains available; nothing was submitted.'); return false;
    }
    const continued = await openConfirmation(stop.order, true);
    if (!ownsRecovery(opening) || !continued) return false;
    recoveryOpeningRef.current = null; setRecoveryOpening(null);
    return true;
  };
  const handleSavedCopyDeleted = useCallback(async (selection) => {
    if (!mounted.current) return;
    outboxRead.current += 1;
    setUnsentRecords((records) => records.filter((record) => record?.orderId !== selection.orderId
      || (record.localRevision && record.localRevision !== selection.localRevision)));
    if (await loadOutbox() === false && mounted.current && sessionBinding?.revision === getActiveSessionRevision()) {
      toast.error('The saved copy is gone, but the remaining list could not refresh. Retry saved deliveries; do not delete again.');
    }
  }, [loadOutbox, sessionBinding]);
  const handlePreparedCopyDeleted = useCallback(async (selection) => {
    if (!mounted.current || sessionBinding !== currentContext.current.sessionBinding
        || sessionBinding?.revision !== getActiveSessionRevision()
        || selection.principal !== deliveryPrincipalKey(sessionBinding)) return;
    outboxRead.current += 1;
    setPreparedSnapshot((snapshot) => snapshot?.principal !== selection.principal ? snapshot : {
      ...snapshot, records: snapshot.records.filter((record) => record.orderId !== selection.orderId
        || record.kind !== selection.kind || record.revision !== selection.revision),
    });
    if (await loadOutbox() === false && mounted.current && sessionBinding?.revision === getActiveSessionRevision()) {
      toast.error('The photo draft is gone, but the list could not refresh. Retry saved deliveries; do not discard again.');
    }
  }, [loadOutbox, sessionBinding]);
  const unsentByOrderId = useMemo(
    () => new Map(unsentRecords.filter((r) => typeof r?.orderId === 'string').map((r) => [r.orderId, r])),
    [unsentRecords]
  );

  // Push notifications
  const {
    supported: pushSupported, subscribed: pushSubscribed, subscribe: pushSubscribe,
    status: pushStatus, pending: pushPending, message: pushMessage,
    canEnable: pushCanEnable, retryStatus: retryPushStatus,
  } = usePushNotifications();
  const [pushDismissed, setPushDismissed] = useState(() => localStorage.getItem('lvb-push-dismissed') === 'true');

  const dateOptions = useMemo(() => getDateOptions(today), [today]);

  const selectedRunStops = selectedRun?.populated_stops || [];
  const totalStops = selectedRunStops.length;

  // Estimated run distance & drive time (Google Routes, no map)
  const { routeInfo, loading: estimateLoading, loadError: estimateLoadError, routeError: estimateRouteError, retryLoading: retryEstimates } = useRunDistance(isHistorical ? NO_STOPS : selectedRunStops);
  const estimateError = estimateLoadError || estimateRouteError;

  // Open synchronously within the click. Device/browser handoff is external.
  // Resume starts at the previous stop, or the configured shop for stop #1.
  const handleResumeFromStop = useCallback((stopIndex) => {
    if (currentContext.current.selectedDate < getTodayString()) return;
    if (!selectedRun?.populated_stops) {
      toast.error('No stops available');
      return;
    }

    const result = buildResumeRouteFromStop(selectedRun.populated_stops, stopIndex, shopOrigin.input);
    if (!result.url || !isGoogleMapsDirectionsUrl(result.url)) {
      toast.error('Could not generate route');
      return;
    }

    if (result.needsSplit) {
      toast.info(`Showing first ${result.stopCount} of ${result.totalRemaining} remaining stops (Google Maps limit)`);
    } else {
      toast.success(`Opening route with ${result.stopCount} stop${result.stopCount !== 1 ? 's' : ''}`);
    }

    window.open(result.url, '_blank', 'noopener,noreferrer');
  }, [selectedRun, shopOrigin.input]);

  const openHistory = order => {
    if (!order?.id || !isHistorical || sessionBinding?.revision !== getActiveSessionRevision()
      || !selectedRunStops.some(stop => stop.order_id === order.id && stop.order?.id === order.id)) return;
    setHistoryOpening({ order, sessionBinding, date: selectedDate, runId: selectedRunId, id: ++openingSequence.current });
  };
  const visibleHistory = isHistorical && historyOpening?.sessionBinding === sessionBinding
    && historyOpening.date === selectedDate && historyOpening.runId === selectedRunId
    && selectedRunStops.some(stop => stop.order_id === historyOpening.order.id && stop.order?.id === historyOpening.order.id)
    ? historyOpening : null;
  // A backgrounded phone can resume after midnight before the timer runs. Do
  // not admit an edit-form event for a date that has become historical.
  const guardEditableEvent = event => {
    if (selectedDate >= getTodayString() && confirmation?.date >= getTodayString()) return;
    event.preventDefault();
    event.stopPropagation();
    openingRequest.current = null;
    confirmationRef.current = null;
    setConfirmation(null);
    setToday(getTodayString());
  };

  return (
    <div className="min-h-screen bg-stone-100" data-testid="driver-runs-view">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Truck className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="font-semibold text-stone-900">My Deliveries</h1>
              <p className="text-xs text-stone-500">{totalStops} stops for {formatDate(selectedDate)}</p>
            </div>
          </div>
        </div>

        {/* Date Tabs */}
        <div className="flex flex-wrap items-center gap-2 px-4 pb-2">
          <Popover open={pastCalendarOpen} onOpenChange={setPastCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant={isHistorical ? 'default' : 'outline'} size="sm" data-testid="driver-past-date">
                <History className="h-4 w-4 mr-1" />Past
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={new Date(`${selectedDate}T00:00:00`)}
                defaultMonth={new Date(`${selectedDate}T00:00:00`)}
                disabled={{ from: new Date(`${today}T00:00:00`) }}
                onSelect={date => {
                  if (!date) return;
                  const chosen = localDateString(date);
                  if (chosen >= getTodayString()) return;
                  setSelectedDate(chosen);
                  setPastCalendarOpen(false);
                }} initialFocus />
            </PopoverContent>
          </Popover>
          <span className="text-sm text-stone-600" data-testid="driver-selected-date">
            {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          {isHistorical && <Button variant="ghost" size="sm" onClick={() => { setToday(getTodayString()); setSelectedDate(getTodayString()); }}>Back to Today</Button>}
        </div>
        <div className="flex gap-1 px-4 pb-3 overflow-x-auto scrollbar-hide">
          {dateOptions.map((date) => (
            <button
              key={date}
              onClick={() => setSelectedDate(date)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedDate === date
                  ? 'bg-primary text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              data-testid={`date-tab-${date}`}
            >
              {formatDate(date)}
            </button>
          ))}
        </div>
      </div>

      {/* Push Notification Banner */}
      {pushSupported && !pushSubscribed && !pushDismissed && pushStatus !== 'offline' && (
        <div className="mx-4 mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="push-notification-banner">
          <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
            <Bell className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900">{pushStatus === 'unverified' ? 'Notification status unverified' : pushStatus === 'denied' ? 'Notifications blocked' : 'Enable notifications'}</p>
            <p className="text-xs text-amber-700 mt-0.5" role="status">{pushMessage || (pushPending ? 'Checking notification setup…' : pushStatus === 'denied' ? 'Allow notifications in browser site settings, then check again.' : 'Get notified when a new run is assigned to you.')}</p>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7 px-3"
                disabled={pushPending || (pushStatus !== 'unverified' && pushStatus !== 'denied' && !pushCanEnable)}
                onClick={() => { if (pushStatus === 'unverified' || pushStatus === 'denied') retryPushStatus(); else pushSubscribe(); }}
                data-testid="push-enable-btn"
              >
                {pushStatus === 'unverified' || pushStatus === 'denied' ? 'Check status' : 'Enable'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-amber-700 text-xs h-7 px-3"
                onClick={() => {
                  setPushDismissed(true);
                  localStorage.setItem('lvb-push-dismissed', 'true');
                }}
                data-testid="push-dismiss-btn"
              >
                <X className="h-3 w-3 mr-1" />
                Later
              </Button>
            </div>
          </div>
        </div>
      )}

      <SavedDeliveryReviewList key={selectedDate} records={unsentRecords} onDeleted={handleSavedCopyDeleted}
        preparedRecords={preparedRecords} onReviewPrepared={reviewPrepared} onPreparedDeleted={handlePreparedCopyDeleted} />
      {outboxError && <div role="alert" className="mx-4 mt-3 rounded-xl bg-blue-50 p-3 text-blue-900">
        <p>Saved deliveries could not be read from this device. Retry to refresh the remaining list.</p>
        <button type="button" className="mt-2 rounded-lg border px-3 py-2" onClick={loadOutbox}>Retry saved deliveries</button>
      </div>}

      {/* Content */}
      <div className="p-4 space-y-4">
        {isHistorical && <p role="status" className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">Past deliveries — view only</p>}
        {hasLoadedRuns && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-stone-500" role="status">
              {loadingRuns ? 'Refreshing runs; showing the last loaded information.' : 'Assigned runs'}
            </p>
            <Button variant="outline" size="sm" onClick={() => fetchRuns()} disabled={loadingRuns} data-testid="driver-runs-refresh">
              Refresh
            </Button>
          </div>
        )}
        {runsError && (
          <Card className="p-4 border-amber-300 bg-amber-50" role="alert">
            <p className="font-medium text-amber-900">
              {hasLoadedRuns ? 'Could not refresh runs. Showing the last loaded information.' : 'Could not load runs.'}
            </p>
            <p className="text-sm text-amber-800 mt-1">{runsError}</p>
            <Button className="mt-3" variant="outline" onClick={() => fetchRuns()} disabled={loadingRuns} data-testid="driver-runs-retry">
              Retry
            </Button>
          </Card>
        )}
        {!hasLoadedRuns ? (
          !runsError && <div className="text-center py-12 text-stone-400" role="status">Loading...</div>
        ) : runs.length === 0 ? (
          <Card className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500 font-medium">No runs assigned</p>
            <p className="text-sm text-stone-400 mt-1">Check back later or select a different date</p>
          </Card>
        ) : (
          <>
            {/* Run Selector (if multiple runs) */}
            {runs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                {runs.map((run) => (
                  <button
                    key={run.id}
                    onClick={() => setSelectedRunId(run.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                      selectedRunId === run.id
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-white text-stone-600 border border-stone-200'
                    }`}
                  >
                    {run.name} ({run.populated_stops?.length || 0} stops)
                  </button>
                ))}
              </div>
            )}

            {/* Selected Run Details */}
            {selectedRun && (
              <div className="space-y-3">
                {/* Run Header Card */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h2 className="font-semibold text-stone-900">{selectedRun.name}</h2>
                      <p className="text-sm text-stone-500">{totalStops} stops</p>
                    </div>
                    {selectedRun.status === 'committed' && (
                      <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        <CheckCircle className="h-3 w-3" />
                        Ready
                      </span>
                    )}
                  </div>

                  {/* Estimates row: distance, drive time, editable fuel cost */}
                  {!isHistorical && estimateLoading && <p role="status" className="mb-3 text-sm text-stone-500">Loading route estimates...</p>}
                  {!isHistorical && estimateError && (
                    <div className="mb-3 text-sm text-stone-600" data-testid="driver-estimates-unavailable">
                      <span role="status">{estimateError.message || 'Google route estimates are unavailable.'}</span>
                      {estimateError.retryable && <Button type="button" variant="outline" size="sm" className="ml-2" onClick={retryEstimates} disabled={estimateLoading} data-testid="driver-estimates-retry">Try again</Button>}
                    </div>
                  )}
                  {!isHistorical && routeInfo && (
                    <div className="mb-3" data-testid="driver-run-estimates">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="flex items-center gap-1.5 bg-primary/90 text-white px-3 py-1.5 rounded-xl text-sm font-medium shadow-sm"
                          data-testid="driver-run-distance"
                        >
                          <Route className="h-4 w-4" />
                          {routeInfo.distance} mi
                        </span>
                        <span
                          className="flex items-center gap-1.5 bg-green-500/90 text-white px-3 py-1.5 rounded-xl text-sm font-medium shadow-sm"
                          data-testid="driver-run-duration"
                        >
                          <Clock className="h-4 w-4" />
                          {routeInfo.duration} min
                        </span>
                        <FuelCostEstimator distanceMiles={parseFloat(routeInfo.distance)} />
                      </div>
                      <p className="text-[11px] text-stone-500 mt-1.5 italic" data-testid="driver-run-estimates-disclaimer">
                        Estimates only — distance, drive time, and fuel cost are approximate and may vary with traffic and gas prices.
                      </p>
                    </div>
                  )}

                  {/* Google Maps Button */}
                  {!isHistorical && <GoogleMapsLinks
                    stops={selectedRun.populated_stops} 
                    className="w-full"
                  />}
                </Card>

                {/* Origin */}
                <Card className="p-3 bg-green-50 border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                      S
                    </div>
                    <div>
                      <p className="text-xs text-green-600 font-medium">START</p>
                      <p className="text-sm font-medium text-green-900">Las Vegas Bouquet</p>
                    </div>
                  </div>
                </Card>

                {/* Stops List */}
                <div className="space-y-2">
                  {selectedRun.populated_stops?.map((stop, index) => {
                    const order = stop.order || {};
                    const isDelivered = order.status === 'delivered';
                    const isFailed = order.status === 'failed';
                    const unsentRecord = unsentByOrderId.get(stop.order_id);
                    const isUnsent = !!unsentRecord;
                    const missingResumeOrigin = !shopOrigin.input
                      && (index === 0 || !getFullAddress(selectedRun.populated_stops[index - 1]?.order));

                    return (
                      <Card 
                        key={stop.order_id} 
                        className={`p-3 ${isUnsent ? 'bg-blue-50 border-blue-300' : isDelivered ? 'bg-green-50 border-green-200' : ''}`}
                        data-testid={`stop-card-${stop.order_id}`}
                      >
                        <div className="flex gap-3">
                          {/* Sequence Badge */}
                          <div className={`w-10 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${
                            isUnsent ? 'bg-blue-600' : isDelivered ? 'bg-green-600' : 'bg-slate-700'
                          } text-white`}>
                            {isDelivered ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <>
                                <span className="text-sm font-bold text-green-400">{stop.sequence}</span>
                                <span className="text-[10px] text-white/70">#{order.internal_order_number?.toString().slice(-2) || '?'}</span>
                              </>
                            )}
                          </div>

                          {/* Stop Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className={`font-medium truncate ${isDelivered ? 'text-green-900' : 'text-stone-900'}`}>
                                  {order.recipient_name || 'Unknown'}
                                </p>
                                <p className={`text-sm truncate ${isDelivered ? 'text-green-700' : 'text-stone-500'}`}>
                                  {order.recipient_street_address || 'No address'}
                                </p>
                                {order.recipient_street_address_2 && (
                                  <p className={`text-sm truncate ${isDelivered ? 'text-green-700' : 'text-stone-500'}`}>
                                    {order.recipient_street_address_2}
                                  </p>
                                )}
                                {order.recipient_city && (
                                  <p className={`text-xs ${isDelivered ? 'text-green-600' : 'text-stone-400'}`}>
                                    {order.recipient_city}, {order.recipient_state} {order.recipient_zip_code}
                                  </p>
                                )}
                              </div>
                              {isDelivered && (
                                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                                  Delivered
                                </span>
                              )}
                              {isFailed && (
                                <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                                  Failed
                                </span>
                              )}
                              {isUnsent && (
                                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid={`unsent-badge-${stop.order_id}`}>
                                  Saved
                                </span>
                              )}
                            </div>

                            {/* Phone & Source */}
                            <div className="flex items-center gap-3 mt-2">
                              {order.recipient_phone && (
                                <a
                                  href={`tel:${order.recipient_phone}`}
                                  className={`flex items-center gap-1.5 text-base font-medium py-1 ${isDelivered ? 'text-green-700' : 'text-primary'} hover:underline`}
                                  data-testid={`call-${stop.order_id}`}
                                >
                                  <Phone className="h-4 w-4" />
                                  {order.recipient_phone}
                                </a>
                              )}
                              {order.source && order.source !== 'manual' && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  isDelivered 
                                    ? 'bg-green-200 text-green-800' 
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {order.source.toUpperCase()}
                                </span>
                              )}
                            </div>

                            {/* Phase 2 — Gate / Door codes (renders nothing if none apply) */}
                            {!isHistorical && !isDelivered && !isFailed && !isUnsent && (
                              <AccessCodesSection orderId={stop.order_id} openingId={`${selectedDate}:${selectedRunId}`} addressKey={savedAccessAddressKey(order)} />
                            )}

                            {/* Status-dependent content */}
                            {isHistorical ? (
                              <div className="mt-2 space-y-2">
                                <p className="text-xs text-stone-600">Server status: {order.status || 'Unavailable'}</p>
                                {isUnsent && <p className="text-sm text-blue-800" data-testid={`saved-copy-${stop.order_id}`}>Saved copy on this device — acceptance not verified</p>}
                                {order.id === stop.order_id && <Button size="sm" variant="outline" onClick={() => openHistory(order)} data-testid={`view-confirmation-${stop.order_id}`}>View confirmation</Button>}
                              </div>
                            ) : isUnsent ? (
                              <div className="mt-2 space-y-2">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5" data-testid={`unsent-info-${stop.order_id}`}>
                                  <div className="flex items-center gap-1.5 text-blue-800 font-medium text-sm">
                                    <CloudOff className="h-4 w-4" />
                                    Older saved copy — cannot resend
                                  </div>
                                  <p className="text-xs text-blue-700 mt-1">
                                    The app cannot verify which contents reached the server. Opening this notice does not send or delete the copy.
                                    See the saved-delivery list above. This copy may block other work on the same order.
                                  </p>
                                </div>
                              </div>
                            ) : isDelivered ? (
                              <div className="mt-2">
                                <div className="flex items-start justify-between">
                                  <div className="text-xs text-green-700 space-y-0.5">
                                    {order.delivery_confirmation?.received_by && (
                                      <div>Received by: {order.delivery_confirmation.received_by}</div>
                                    )}
                                    {order.delivery_confirmation?.received_by_relationship && (
                                      <div className="text-green-600">({order.delivery_confirmation.received_by_relationship})</div>
                                    )}
                                    {order.delivery_confirmation?.notes && (
                                      <div className="text-green-600 italic">{order.delivery_confirmation.notes}</div>
                                    )}
                                  </div>
                                  {!selectedRun?.closed_at && (
                                    <button
                                      onClick={() => openConfirmation(order)}
                                      className="p-1.5 rounded-lg text-green-600 hover:bg-green-100 transition-colors flex-shrink-0"
                                      title="Edit delivery info"
                                      data-testid={`edit-pod-${stop.order_id}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : isFailed ? (
                              <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid={`failed-info-${stop.order_id}`}>
                                <div className="text-xs text-red-700 space-y-0.5">
                                  <div className="font-medium">
                                    {order.delivery_failure?.reason === 'nobody_home' && 'Nobody home'}
                                    {order.delivery_failure?.reason === 'wrong_address' && 'Wrong address'}
                                    {order.delivery_failure?.reason === 'other' && 'Other reason'}
                                  </div>
                                  {order.delivery_failure?.details && (
                                    <div className="text-red-600 italic">{order.delivery_failure.details}</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex gap-2 mt-3">
                                {/* Resume from the preceding stop, or the configured shop. */}
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleResumeFromStop(index)}
                                  disabled={missingResumeOrigin}
                                  title={missingResumeOrigin ? 'Shop location unavailable. Ask an administrator to check Shop Profile.' : undefined}
                                  data-testid={`resume-from-${stop.order_id}`}
                                >
                                  <RotateCcw className="h-4 w-4 mr-1.5" />
                                  Directions from here
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  onClick={() => openConfirmation(order)}
                                  data-testid={`mark-delivered-${stop.order_id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1.5" />
                                  Confirm Stop
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Designer's arrangement photo — quick visual ID of
                              what's being delivered; tap to enlarge in the car. */}
                          {order.arrangement_photo && (
                            <ArrangementPhotoThumbnail
                              photo={order.arrangement_photo}
                              className="w-[84px] h-[84px] self-start"
                              testId={`driver-arrangement-thumb-${stop.order_id}`}
                            />
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delivery Confirmation Sheet */}
      <div onClickCapture={guardEditableEvent} onSubmitCapture={guardEditableEvent}>
      <DeliveryConfirmationSheet
        key={confirmation?.id || 'closed'}
        openingId={confirmation?.id}
        sessionBinding={confirmation?.sessionBinding}
        isOpen={!!confirmingOrder}
        onClose={() => {
          if (!confirmation || confirmationRef.current !== confirmation
            || getActiveSessionRevision() !== confirmation.sessionBinding.revision) return;
          confirmationRef.current = null;
          setConfirmation(null);
          loadOutbox();
        }}
        order={confirmingOrder}
        onDeliveryConfirmed={() => {
          if (confirmation && confirmationRef.current === confirmation
            && getActiveSessionRevision() === confirmation.sessionBinding.revision) confirmation.refreshRuns();
        }}
      />
      </div>
      {DELIVERY_PHOTO_PREPARATION_ENABLED && recoveryOpening?.binding === sessionBinding
        && recoveryOpening.binding.revision === getActiveSessionRevision() && <SavedPhotoRecoverySheet
        key={recoveryOpening.id} opening={recoveryOpening}
        onContinue={(record) => continuePrepared(recoveryOpening, record)}
        onChanged={() => { if (ownsRecovery(recoveryOpening)) void loadOutbox(); }}
        onClose={() => {
          if (!ownsRecovery(recoveryOpening)) return;
          openingRequest.current = null;
          recoveryOpeningRef.current = null; setRecoveryOpening(null); void loadOutbox();
        }} />}
      {visibleHistory && <DriverDeliveryHistory
        key={`${visibleHistory.sessionBinding.revision}:${visibleHistory.date}:${visibleHistory.runId}:${visibleHistory.order.id}:${visibleHistory.id}`}
        order={visibleHistory.order} sessionBinding={visibleHistory.sessionBinding}
        onClose={() => setHistoryOpening(current => current === visibleHistory ? null : current)}
      />}
    </div>
  );
};

export default DriverRunsView;
